import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import SEO from '../components/SEO'
import { projectData, tagColors } from '../data/portfolioData'
import { FaArrowLeft, FaExternalLinkAlt, FaCheck } from 'react-icons/fa'

const ProjectDetail = () => {
  const { category, id } = useParams()
  const navigate = useNavigate()
  const [selectedImage, setSelectedImage] = useState(0)

  const projects = projectData[category] || []
  const project = projects[parseInt(id, 10)]

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [category, id])

  useEffect(() => {
    if (!project) {
      navigate('/portfolio')
    }
  }, [project, navigate])

  if (!project) return null

  return (
    <div>
      <SEO title={`${project.title} - Portfolio`} description={project.description.slice(0, 150) + '...'} />
      <section className="py-12">
        <div className="container-main">
          <button
            onClick={() => navigate('/portfolio')}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-[#121212] px-4 py-2 text-sm font-semibold text-white hover:border-[rgb(249,193,37)] hover:text-[rgb(249,193,37)]"
          >
            <FaArrowLeft className="h-3.5 w-3.5" />
            Back to Portfolio
          </button>

          <div className="neo-card p-7 md:p-10">
            <span className="section-kicker">{project.category}</span>
            <h1 className="mt-4 text-4xl font-bold md:text-6xl">{project.title}</h1>
            <p className="mt-4 max-w-4xl text-base leading-relaxed text-[#aaaaaa] md:text-lg">{project.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {project.tags.map((tag, idx) => (
                <span key={`${tag}-${idx}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${tagColors[tag] || 'bg-[#272727] text-white'}`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="container-main grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <div>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img
                src={project.image[selectedImage]}
                alt={`${project.title} view ${selectedImage + 1}`}
                className="w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://via.placeholder.com/1200x800/1a1a1a/c8e64a?text=${encodeURIComponent(project.title)}`
                }}
              />
            </div>

            {project.image.length > 1 && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {project.image.map((img, idx) => (
                  <button
                    key={img + idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`overflow-hidden rounded-xl border ${selectedImage === idx ? 'border-[rgb(249,193,37)]' : 'border-white/10'}`}
                  >
                    <img src={img} alt={`thumb ${idx + 1}`} className="h-20 w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="neo-card p-6">
              <h3 className="text-xl font-semibold text-white">Project Info</h3>
              <div className="mt-4 space-y-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-[#8f8f8f]">Category</p>
                  <p className="mt-1 font-semibold text-white">{project.category}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-[#8f8f8f]">Project Type</p>
                  <p className="mt-1 font-semibold text-white">Custom Development</p>
                </div>
              </div>
            </div>

            <a
              href={project.liveUrl || '#'}
              target={project.liveUrl ? '_blank' : undefined}
              rel={project.liveUrl ? 'noopener noreferrer' : undefined}
              className="btn-primary w-full"
            >
              View Live Demo
              <FaExternalLinkAlt className="h-3.5 w-3.5" />
            </a>
          </aside>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-main">
          <div className="neo-card p-7 md:p-10">
            <h2 className="text-3xl font-semibold text-white">Project Overview</h2>
            <p className="mt-4 text-base leading-relaxed text-[#ababab] md:text-lg">{project.description}</p>
            <h3 className="mt-8 text-xl font-semibold text-white">Key Features</h3>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {project.tags.map((tag, idx) => (
                <li key={`${tag}-${idx}`} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#111111] p-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(249,193,37)] text-[10px] text-[#101010]">
                    <FaCheck />
                  </span>
                  <span className="text-sm text-[#bcbcbc]">{tag} integration and implementation</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ProjectDetail

